import React, { useMemo } from 'react';
import {
  playSuccessSound,
  playFailureSound,
  isMaterialAnswerCorrect,
  createEmptyMaterialProgress,
  seededShuffle,
} from '../utils/materialUtils';
import OrderWordsExercise from './OrderWordsExercise';
import CheckboxExercise from './CheckboxExercise';
import './InteractiveMaterial.css';

const InteractiveMaterial = ({
  title,
  content = [],
  materialId,
  progress = createEmptyMaterialProgress(),
  onProgressChange,
  silent = false,
  readOnly = false,
}) => {
  const safeContent = Array.isArray(content) ? content : [];

  const orderPoolLayouts = useMemo(() => {
    const layouts = {};
    safeContent.forEach((segment, index) => {
      if (segment.type === 'order') {
        const tokenIds = (segment.tokens || []).map((token) => token.id);
        layouts[index] = seededShuffle(tokenIds, `${materialId ?? 'material'}-${index}`);
      }
    });
    return layouts;
  }, [safeContent, materialId]);

  const updateProgress = (updater) => {
    if (readOnly || !onProgressChange) return;
    const next = typeof updater === 'function'
      ? updater(progress)
      : updater;
    onProgressChange(next);
  };

  const handleGapChange = (gapIndex, value) => {
    const segment = safeContent[gapIndex];
    if (!segment || segment.type !== 'gap') return;

    const gapState = progress.gaps[gapIndex] || progress.gaps[String(gapIndex)] || {};
    if (gapState.status === 'correct') return;
    if (gapState.value === value) return;

    const isCorrect = value === segment.correct;
    const status = isCorrect ? 'correct' : 'incorrect';

    updateProgress((prev) => ({
      ...prev,
      gaps: {
        ...prev.gaps,
        [gapIndex]: { value, status },
      },
    }));

    if (!silent) {
      if (isCorrect) playSuccessSound();
      else playFailureSound();
    }
  };

  const handleInputChange = (inputIndex, value) => {
    const inputState = progress.inputs[inputIndex] || progress.inputs[String(inputIndex)] || {};
    if (inputState.status === 'correct') return;
    updateProgress((prev) => ({
      ...prev,
      inputs: {
        ...prev.inputs,
        [inputIndex]: {
          value,
          status: prev.inputs[inputIndex]?.status || null,
        },
      },
    }));
  };

  const handleInputCheck = (inputIndex) => {
    const segment = safeContent[inputIndex];
    if (!segment || segment.type !== 'input') return;

    const inputState = progress.inputs[inputIndex] || progress.inputs[String(inputIndex)] || {};
    if (inputState.status === 'correct') return;

    const value = inputState.value || '';
    if (!value.trim()) return;

    const isCorrect = isMaterialAnswerCorrect(value, segment.correct);

    updateProgress((prev) => ({
      ...prev,
      inputs: {
        ...prev.inputs,
        [inputIndex]: {
          value: isCorrect ? segment.correct : value,
          status: isCorrect ? 'correct' : 'incorrect',
        },
      },
    }));

    if (!silent) {
      if (isCorrect) playSuccessSound();
      else playFailureSound();
    }
  };

  const handleInputKeyDown = (event, inputIndex) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleInputCheck(inputIndex);
    }
  };

  const handleOrderChange = (orderIndex, answerIds, status) => {
    updateProgress((prev) => ({
      ...prev,
      orders: {
        ...prev.orders,
        [orderIndex]: { answerIds, status },
      },
    }));
  };

  const handleCheckboxChange = (checkboxIndex, selectedIds, itemStatus, status) => {
    updateProgress((prev) => ({
      ...prev,
      checkboxes: {
        ...prev.checkboxes,
        [checkboxIndex]: { selectedIds, itemStatus, status },
      },
    }));
  };

  if (safeContent.length === 0) {
    return (
      <div className="interactive-material-empty">
        <p>Текст материала пуст</p>
      </div>
    );
  }

  let gapCounter = 0;
  let inputCounter = 0;

  return (
    <div className="interactive-material">
      {title && <h2 className="interactive-material-title">{title}</h2>}
      <div className="interactive-material-body">
        {safeContent.map((segment, index) => {
          if (segment.type === 'text') {
            return (
              <span key={`text-${index}`} className="interactive-material-text">
                {segment.value}
              </span>
            );
          }

          if (segment.type === 'gap') {
            const gapIndex = index;
            const gapState = progress.gaps[gapIndex] || progress.gaps[String(gapIndex)] || {};
            const status = gapState.status;
            const isLocked = status === 'correct' || readOnly;
            gapCounter += 1;

            return (
              <span key={`gap-${index}`} className="interactive-material-gap-wrap">
                <select
                  className={`interactive-material-gap ${status || ''}`}
                  value={gapState.value || ''}
                  disabled={isLocked}
                  onChange={(e) => handleGapChange(gapIndex, e.target.value)}
                  aria-label={`Вариант ответа ${gapCounter}`}
                >
                  <option value="" disabled>
                    ...
                  </option>
                  {(segment.options || []).map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </span>
            );
          }

          if (segment.type === 'input') {
            const inputIndex = index;
            const inputState = progress.inputs[inputIndex] || progress.inputs[String(inputIndex)] || {};
            const status = inputState.status;
            const isLocked = status === 'correct' || readOnly;
            inputCounter += 1;

            return (
              <span key={`input-${index}`} className="interactive-material-input-wrap">
                <input
                  type="text"
                  className={`interactive-material-input ${status || ''}`}
                  value={inputState.value || ''}
                  placeholder={segment.hint}
                  disabled={isLocked}
                  readOnly={isLocked}
                  onChange={(e) => handleInputChange(inputIndex, e.target.value)}
                  onBlur={() => handleInputCheck(inputIndex)}
                  onKeyDown={(e) => handleInputKeyDown(e, inputIndex)}
                  aria-label={`Введите форму слова ${inputCounter}`}
                  style={{ width: `${Math.max(segment.hint?.length || 8, segment.correct?.length || 8, 6) + 2}ch` }}
                />
              </span>
            );
          }

          if (segment.type === 'order') {
            const orderIndex = index;
            const orderState = progress.orders[orderIndex] || progress.orders[String(orderIndex)] || {};

            return (
              <div key={`order-${index}`} className="interactive-material-order-wrap">
                <OrderWordsExercise
                  segment={{
                    ...segment,
                    poolLayoutIds: orderPoolLayouts[orderIndex],
                  }}
                  answerIds={orderState.answerIds || []}
                  status={orderState.status || null}
                  silent={silent}
                  readOnly={readOnly}
                  onAnswerChange={(answerIds) => handleOrderChange(
                    orderIndex,
                    answerIds,
                    orderState.status || null
                  )}
                  onStatusChange={(nextStatus) => handleOrderChange(
                    orderIndex,
                    orderState.answerIds || [],
                    nextStatus
                  )}
                />
              </div>
            );
          }

          if (segment.type === 'checkbox') {
            const checkboxIndex = index;
            const checkboxState = progress.checkboxes[checkboxIndex] || progress.checkboxes[String(checkboxIndex)] || {};

            return (
              <div key={`checkbox-${index}`} className="interactive-material-checkbox-wrap">
                <CheckboxExercise
                  segment={segment}
                  selectedIds={checkboxState.selectedIds || []}
                  itemStatus={checkboxState.itemStatus || {}}
                  status={checkboxState.status || null}
                  silent={silent}
                  readOnly={readOnly}
                  onSelectionChange={(selectedIds, itemStatus, status) => {
                    handleCheckboxChange(checkboxIndex, selectedIds, itemStatus, status);
                  }}
                />
              </div>
            );
          }

          return null;
        })}
      </div>
    </div>
  );
};

export default InteractiveMaterial;
